/**
 * @fileoverview Generic overlay dialog shell.
 * @description The one dialog/panel chrome used across the app — a dimmed backdrop, a centered
 * white card with a title/subtitle header and corner close button (via IconButton's existing
 * "modal-close" variant), a scrollable body for the caller's content, and an optional footer for
 * action buttons. Closing on Escape and on a backdrop click are both wired in, with clicks inside
 * the card itself stopped from bubbling to the backdrop. Sales module detail views, the "New
 * Opportunity" form, and the "Add User" form all render inside this shell rather than each
 * building their own overlay.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect } from 'react'

import IconButton from '@/shared/components/atoms/IconButton'

import type { MouseEvent, ReactNode } from 'react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg'
  'data-testid'?: string
}

// Thin, light scrollbar (standard properties, supported by current Chrome, Edge, Firefox and
// Safari) shared by the dialog's scrollable regions, so a long body doesn't show a heavy default bar.
const SCROLLBAR_CLASS_NAMES =
  '[scrollbar-width:thin] [scrollbar-color:var(--color-input-border)_transparent]'

const SIZE_CLASS_NAMES: Record<NonNullable<ModalProps['size']>, string> = {
  md: 'max-w-[560px]',
  lg: 'max-w-[820px]',
}

/**
 * @description Stops a click inside the dialog card from bubbling to the backdrop's onClose.
 * @param {MouseEvent<HTMLDivElement>} event - The card's click event.
 */
function stopPropagation(event: MouseEvent<HTMLDivElement>) {
  event.stopPropagation()
}

/**
 * @component
 * @param {ModalProps} props - Open state, close handler, and the dialog's content.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called on Escape, a backdrop click, or the corner close
 * button.
 * @param {string} props.title - The dialog's heading.
 * @param {ReactNode} [props.subtitle] - An optional line under the heading — usually plain text,
 * but any node (e.g. text plus a status Badge) is fine too.
 * @param {ReactNode} props.children - The dialog's scrollable body content.
 * @param {ReactNode} [props.footer] - Action buttons rendered in a bottom bar, if provided.
 * @param {'md' | 'lg'} [props.size] - "md" (default, 560px) or "lg" (820px) max width.
 * @param {string} [props['data-testid']] - QA locator for the dialog card. The title, close button
 * and footer get the same id with "-title", "-close-button" and "-footer" appended.
 */
function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  'data-testid': testId,
}: ModalProps) {
  useEffect(() => {
    if (!open) {
      return
    }

    /**
     * @description Closes the dialog when the visitor presses Escape.
     * @param {KeyboardEvent} event - The document keydown event.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) {
    return null
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/55 px-5 py-10 ${SCROLLBAR_CLASS_NAMES}`}
      onClick={onClose}
    >
      <div
        data-testid={testId}
        className={`relative flex max-h-[85vh] w-full flex-col overflow-hidden rounded-xl bg-white shadow-[0_24px_60px_rgba(11,26,52,0.32)] ${SIZE_CLASS_NAMES[size]}`}
        onClick={stopPropagation}
      >
        <div className="flex items-start gap-4 border-b border-divider px-6 py-5">
          <div className="min-w-0 flex-1">
            <h2
              className="text-[18px] font-bold text-ink"
              data-testid={testId ? `${testId}-title` : undefined}
            >
              {title}
            </h2>
            {subtitle && <div className="mt-0.5 text-sm text-slate">{subtitle}</div>}
          </div>
          <IconButton
            variant="modal-close"
            aria-label="Close"
            onClick={onClose}
            data-testid={testId ? `${testId}-close-button` : undefined}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              className="text-slate"
              aria-hidden="true"
            >
              <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
            </svg>
          </IconButton>
        </div>

        <div className={`flex-1 overflow-y-auto px-6 py-5 ${SCROLLBAR_CLASS_NAMES}`}>
          {children}
        </div>

        {footer && (
          <div
            className="flex items-center justify-end gap-2.5 border-t border-divider bg-input-bg px-6 py-4"
            data-testid={testId ? `${testId}-footer` : undefined}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export default Modal
